/**
 * ContractorPortalPage
 * --------------------
 * Trade-partner portal. A contractor sees only the jobs they're assigned to
 * and only the CSI divisions the Project Manager granted them — an electrician
 * can add electrical pricing, a plumber only plumbing. They can also upload
 * their licence, insurance and certifications. Nothing else is editable.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  HardHat, Loader2, Plus, Trash2, Save, Upload, FileText, Building2, UserRound, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import ContractorChangeOrders from "@/components/contractor/ContractorChangeOrders";
import ContractorSubmittalsRfis from "@/components/contractor/ContractorSubmittalsRfis";
import ContractorFieldLog from "@/components/contractor/ContractorFieldLog";
import ContractorMyJobs from "@/components/contractor/ContractorMyJobs";
import { API as API_BASE } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ContractorPortalPage() {
  const { user, token, logout } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [jobs, setJobs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myProjects, setMyProjects] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/change-orders/mine`, { headers })
      .then((r) => setMyProjects(r.data?.projects || []))
      .catch(() => setMyProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/contractor/assignments`, { headers });
      const list = r.data?.assignments || [];
      setJobs(list);
      if (list.length && !activeId) {
        setActiveId(list[0].id);
        setDivisions(seedDivisions(list[0]));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your assignments");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const active = jobs.find((j) => j.id === activeId) || null;
  const linkedProject = myProjects.find((p) => p.source_quote?.id === activeId) || myProjects[0] || null;

  function seedDivisions(job) {
    const existing = job.my_divisions || [];
    return (job.allowed_division_codes || []).map((code) => {
      const found = existing.find((d) => (d.code || "").trim() === code);
      return found
        ? { ...found, items: (found.items || []).map((i) => ({ ...i })) }
        : { code, title: "", responsible: "", items: [] };
    });
  }

  const pick = (job) => { setActiveId(job.id); setDivisions(seedDivisions(job)); };

  const myTotal = divisions.reduce((s, d) =>
    s + (d.items || []).reduce((ds, li) => ds + Number(li.qty || 0) * Number(li.unit_price || 0), 0), 0);

  const itemSet = (di, ii, key, v) => setDivisions((ds) => ds.map((d, i) => i !== di ? d : {
    ...d, items: d.items.map((li, j) => (j === ii ? { ...li, [key]: v } : li)),
  }));

  const submit = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/contractor/proposals/${activeId}/divisions`, { divisions }, { headers });
      toast.success(`Submitted — ${(r.data?.saved_codes || []).join(", ")}`, {
        description: "The project manager can see your pricing now.",
      });
      setJobs((prev) => prev.map((j) => (j.id === activeId ? r.data.view : j)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your pricing");
    } finally { setSaving(false); }
  };

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/api/contractor/proposals/${activeId}/documents?kind=${kind}`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const myDocs = (active?.my_contractor_records || []).flatMap((c) => c.documents || []);
  const myRecord = (active?.my_contractor_records || [])[0] || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-portal">
      <header className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/20 ring-1 ring-amber-400/40 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-400">Trade Partner Portal</p>
              <h1 className="text-lg font-extrabold">Sterile Mate Project Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-300"><UserRound className="w-4 h-4" />{user?.name || user?.email}</span>
            <Button size="sm" variant="outline" className="text-slate-900" onClick={logout} data-testid="contractor-logout">
              <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6">
        <div className="mb-5">
          <ContractorMyJobs token={token} />
        </div>
        {loading ? (
          <p className="text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading your jobs…</p>
        ) : jobs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="contractor-no-jobs">
            <p className="font-bold text-slate-800">No job assignments yet</p>
            <p className="text-sm text-slate-500 mt-1">Once the project manager assigns you to a job, it will appear here and you can upload your proposal and licences.</p>
          </div>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap mb-5">
              {jobs.map((j) => (
                <button key={j.id} onClick={() => pick(j)}
                  className={`text-left px-4 py-2.5 rounded-lg border transition-colors ${j.id === activeId ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-amber-500"}`}
                  data-testid={`contractor-job-${j.id}`}>
                  <span className="block text-[10px] font-black uppercase tracking-wider opacity-70">{j.proposal_number || j.id}</span>
                  <span className="block text-sm font-bold">{j.project_name}</span>
                </button>
              ))}
            </div>

            {active && (
              <>
                <section className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
                  <div className="flex flex-wrap gap-6 items-start justify-between">
                    <div>
                      <h2 className="text-base font-extrabold text-slate-900 inline-flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-500" />{active.project_name}</h2>
                      <p className="text-sm text-slate-600">{active.owner_name}{active.facility ? ` · ${active.facility}` : ""}{active.location ? ` · ${active.location}` : ""}</p>
                      {active.job_number ? <p className="text-xs text-slate-400">{active.job_number}</p> : null}
                      <p className="text-xs text-slate-500 mt-2">
                        Your scope: <strong>{(active.allowed_division_codes || []).join(", ") || "not assigned yet"}</strong>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase font-black tracking-wider text-slate-500">Your proposal total</p>
                      <p className="text-2xl font-black text-slate-900" data-testid="contractor-my-total">{fmt$(myTotal)}</p>
                      {active.pm_name ? <p className="text-xs text-slate-500 mt-1">PM: {active.pm_name}{active.pm_email ? ` · ${active.pm_email}` : ""}</p> : null}
                    </div>
                  </div>
                  {active.scope_description ? <p className="text-xs text-slate-500 mt-3 border-t border-slate-200 pt-3">{active.scope_description}</p> : null}
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">Your proposal line items</h3>
                    <Button onClick={submit} disabled={saving || divisions.length === 0} className="bg-amber-600 hover:bg-amber-700" size="sm" data-testid="contractor-submit">
                      {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Submit to project manager
                    </Button>
                  </div>
                  {divisions.length === 0 ? (
                    <p className="text-sm text-slate-400">The project manager hasn&rsquo;t assigned a scope to you on this job yet.</p>
                  ) : divisions.map((d, di) => {
                    const dTotal = (d.items || []).reduce((s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0);
                    return (
                      <div key={d.code} className="rounded-lg border border-slate-200 mb-3" data-testid={`contractor-div-${d.code.replace(/\s/g, "")}`}>
                        <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                          <p className="text-sm font-bold text-slate-800">{d.code}{d.title ? ` · ${d.title}` : ""}</p>
                          <p className="text-sm font-black text-slate-900">{fmt$(dTotal)}</p>
                        </div>
                        <div className="p-3">
                          {(d.items || []).map((li, ii) => (
                            <div key={ii} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-1.5 items-center">
                              <Input className="sm:col-span-6" placeholder="Description of work" value={li.description || ""} onChange={(e) => itemSet(di, ii, "description", e.target.value)} data-testid={`contractor-item-desc-${di}-${ii}`} />
                              <Input className="sm:col-span-1" type="number" placeholder="Qty" value={li.qty ?? ""} onChange={(e) => itemSet(di, ii, "qty", Number(e.target.value))} />
                              <Input className="sm:col-span-1" placeholder="UOM" value={li.uom || ""} onChange={(e) => itemSet(di, ii, "uom", e.target.value)} />
                              <Input className="sm:col-span-2" type="number" placeholder="Unit $" value={li.unit_price ?? ""} onChange={(e) => itemSet(di, ii, "unit_price", Number(e.target.value))} />
                              <span className="sm:col-span-1 text-xs font-bold text-slate-700 text-right">{fmt$(Number(li.qty || 0) * Number(li.unit_price || 0))}</span>
                              <Button size="icon" variant="ghost" className="sm:col-span-1" onClick={() => setDivisions((ds) => ds.map((x, j) => j === di ? { ...x, items: x.items.filter((_, k) => k !== ii) } : x))}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                          <Button size="sm" variant="outline" onClick={() => setDivisions((ds) => ds.map((x, j) => j === di ? { ...x, items: [...(x.items || []), { description: "", qty: 1, uom: "LS", unit_price: 0 }] } : x))} data-testid={`contractor-add-item-${di}`}>
                            <Plus className="w-3 h-3 mr-1" /> Add line item
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-sm font-black uppercase tracking-wider text-slate-800 mb-1">Licences &amp; certifications</h3>
                  <p className="text-xs text-slate-500 mb-3">
                    {myRecord.license_type ? `${myRecord.license_type} ${myRecord.license_number || ""}` : "Upload your licence, insurance certificate and any certifications."}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {myDocs.map((d) => (
                      <span key={d.id} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 ring-1 ring-slate-300 rounded-full px-2 py-1">
                        <FileText className="w-3 h-3" />
                        <a href={`${API}${d.url}`} target="_blank" rel="noreferrer" className="hover:underline">{d.kind}: {d.name}</a>
                      </span>
                    ))}
                    {["license", "insurance", "certification"].map((kind) => (
                      <label key={kind} className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md ring-1 ring-slate-300 cursor-pointer hover:ring-amber-500" data-testid={`contractor-upload-${kind}`}>
                        <Upload className="w-3 h-3" /> Upload {kind}
                        <input type="file" className="hidden" accept="application/pdf,image/*"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, kind); e.target.value = ""; }} />
                      </label>
                    ))}
                  </div>
                </section>

                <ContractorChangeOrders
                  token={token}
                  proposalId={active.id}
                  tradeName={myRecord.company || user?.company_name || user?.name}
                />

                <ContractorSubmittalsRfis
                  token={token}
                  projectId={linkedProject?.id}
                  projectName={linkedProject?.name || active.project_name}
                  tradeName={myRecord.company || user?.company_name || user?.name}
                />

                <ContractorFieldLog
                  token={token}
                  projectId={linkedProject?.id}
                  projectName={linkedProject?.name || active.project_name}
                  tradeName={myRecord.company || user?.company_name || user?.name}
                />
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
